/**
 * @fileoverview The substitute's page (`/sub/:token`, docs/substitute-requests.md).
 *
 * Someone the organization asked to cover a class — usually with no Hans
 * account — opens this from the email they were sent. It answers what they
 * need in the order they need it: where and when, who to call, what was left
 * for them (the organization's note and plan, the instructor's lesson plan),
 * and then one form to write the session's notes. Those notes are the
 * session's lesson on the organization's side; the org reviews them and
 * decides whether families see them.
 *
 * **The link is the credential**, so the page asks for nothing else and shows
 * nothing it does not need to: no student names and no contact details beyond
 * the program's own contacts. It keeps working for the life of the program and
 * stops when the program ends or the organization sends a fresh link.
 *
 * Unlisted, like the consent pages: kept out of search indexes, and read with
 * a plain client that never redirects to a portal sign-in.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button, Card, SectionHeader, Tag } from "@/components/studio/primitives";
import { dayLabel } from "@/lib/orgReporting";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-3 font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

function clock(time) {
  if (!time) return "";
  const [h, m] = String(time).split(":").map(Number);
  if (Number.isNaN(h)) return time;
  return new Date(2000, 0, 1, h, m || 0).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function weekday(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y) return "";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "long" });
}

/** A detail line: a label in small caps and its value, only when there is one. */
function Fact({ label, children, testId }) {
  if (!children) return null;
  return (
    <div data-testid={testId}>
      <dt className="font-sans text-[12px] font-semibold text-[var(--text-tertiary)]">{label}</dt>
      <dd className="mt-0.5 font-sans text-[15px] text-[var(--text-primary)]">{children}</dd>
    </div>
  );
}

/** Something written for the substitute, kept as its author wrote it. */
function Written({ title, by, children, testId }) {
  return (
    <Card className="mb-4" data-testid={testId}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-sans text-[16px] font-semibold text-[var(--text-primary)]">{title}</h3>
        {by && <span className="font-sans text-[12px] text-[var(--text-tertiary)]">{by}</span>}
      </div>
      <div className="mt-2 whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-[var(--text-secondary)]">{children}</div>
    </Card>
  );
}

const ERRORS = {
  link_expired:
    "This link has stopped working — the program has ended, or the organization sent you a newer link. Ask your contact at the organization if you still need it.",
  link_invalid: "This link isn't valid. Check you opened the whole link from your email, or ask the organization to send it again.",
};

export default function SubstituteSessionPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Unlisted: only reachable through the emailed link.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const apply = useCallback((payload) => {
    setData(payload);
    setTitle(payload?.lesson?.title || payload?.class?.name || "");
    setNotes(payload?.lesson?.notes || "");
    setEditing(!payload?.lesson);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    axios
      .get(`${API}/substitute/${encodeURIComponent(token)}`, { signal: controller.signal })
      .then((res) => apply(res.data))
      .catch((err) => {
        if (controller.signal.aborted) return;
        const code = err?.response?.data?.detail?.code;
        const status = err?.response?.status;
        setError(ERRORS[code] || (status === 410 ? ERRORS.link_expired : status === 404 ? ERRORS.link_invalid : "This page couldn't be loaded. Try again in a moment."));
      });
    return () => controller.abort();
  }, [token, apply]);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const body = { title: title.trim() || data.class?.name || "Class session", notes: notes.trim() };
      const url = `${API}/substitute/${encodeURIComponent(token)}/lesson`;
      const res = data.lesson ? await axios.put(url, body) : await axios.post(url, body);
      apply(res.data);
    } catch (err) {
      setSaveError(err?.response?.data?.detail?.message || "Your notes couldn't be saved. Copy them somewhere safe and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen bg-[var(--studio-base)]" data-testid="substitute-page">
      <div className="mx-auto max-w-[760px] px-4 pb-16 pt-10 md:px-8">
        <header className="mb-8 flex items-center gap-3">
          {data?.org?.logo_url ? (
            <img src={data.org.logo_url} alt="" className="h-9 w-9 rounded-lg object-contain" />
          ) : (
            <img src="/logo192.png" alt="" className="h-9 w-9 rounded-lg" />
          )}
          <span className="font-sans text-[15px] font-medium text-[var(--text-secondary)]">{data?.org?.name || "Hans"}</span>
        </header>

        {error ? (
          <Card data-testid="substitute-error">
            <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">This link isn&rsquo;t available</h1>
            <p className="mt-2 font-sans text-[15px] text-[var(--text-secondary)]">{error}</p>
          </Card>
        ) : !data ? (
          <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>
        ) : (
          <>
            <p className="font-sans text-[13px] font-medium text-[var(--accent-yellow-text)]">You&rsquo;re covering</p>
            <h1 className="mt-1 font-serif text-3xl font-semibold text-[var(--text-primary)]" data-testid="substitute-class">
              {data.class?.name}
            </h1>
            <p className="mt-2 font-sans text-[16px] text-[var(--text-secondary)]" data-testid="substitute-when">
              {weekday(data.session?.date)}, {dayLabel(data.session?.date, { year: true })}
              {data.session?.time_local ? ` · ${clock(data.session.time_local)}` : ""}
              {data.session?.duration_minutes ? ` · ${data.session.duration_minutes} min` : ""}
              {data.session?.timezone ? <span className="text-[var(--text-tertiary)]"> ({data.session.timezone})</span> : null}
            </p>

            {/* ---- Where, and for whom ------------------------------- */}
            <Card className="mt-6" data-testid="substitute-details">
              <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                <Fact label="Program" testId="substitute-program">{data.program?.title}</Fact>
                <Fact label="Usually taught by" testId="substitute-instructor">{data.instructor_name}</Fact>
                <Fact label="Where" testId="substitute-venue">
                  {data.venue?.address_line ? (
                    data.venue.map_url ? (
                      <a href={data.venue.map_url} target="_blank" rel="noopener noreferrer" className="text-[var(--accent-yellow-text)] hover:underline">
                        {data.venue.address_line}
                      </a>
                    ) : (
                      data.venue.address_line
                    )
                  ) : null}
                </Fact>
                <Fact label="Room" testId="substitute-room">{data.class?.room}</Fact>
              </dl>
              {data.class?.notes && (
                <p className="mt-4 whitespace-pre-wrap font-sans text-[14px] text-[var(--text-secondary)]" data-testid="substitute-class-notes">
                  {data.class.notes}
                </p>
              )}
            </Card>

            {/* ---- Who to call ----------------------------------------- */}
            {(data.contacts || []).length > 0 && (
              <>
                <SectionHeader className="mt-8">Points of contact</SectionHeader>
                <Card padding={0} data-testid="substitute-contacts">
                  {data.contacts.map((c, i) => (
                    <div
                      key={`${c.email || c.name}-${i}`}
                      className={`px-5 py-4 ${i < data.contacts.length - 1 ? "border-b border-[var(--studio-divider)]" : ""}`}
                    >
                      <div className="font-sans text-[15px] font-medium text-[var(--text-primary)]">
                        {c.name}
                        {c.role && <span className="font-normal text-[var(--text-tertiary)]"> · {c.role}</span>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-x-4 font-sans text-[14px]">
                        {c.email && (
                          <a href={`mailto:${c.email}`} className="text-[var(--accent-yellow-text)] hover:underline">
                            {c.email}
                          </a>
                        )}
                        {c.phone && (
                          <a href={`tel:${c.phone}`} className="text-[var(--accent-yellow-text)] hover:underline">
                            {c.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </Card>
              </>
            )}

            {/* ---- What was left for them ------------------------------ */}
            {(data.assignment_note || data.org_note || data.plan || data.lesson_plan || data.instructor_prep) && (
              <SectionHeader className="mt-8">Left for you</SectionHeader>
            )}
            {data.assignment_note && (
              <Written title="Note from the organization" testId="substitute-assignment-note">
                {data.assignment_note}
              </Written>
            )}
            {data.org_note && (
              <Written title="About this session" by={data.org?.name} testId="substitute-org-note">
                {data.org_note}
              </Written>
            )}
            {data.plan && (
              <Card className="mb-4" data-testid="substitute-plan-file">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-sans text-[16px] font-semibold text-[var(--text-primary)]">Session plan</div>
                    <div className="font-sans text-[13px] text-[var(--text-tertiary)]">{data.plan.filename}</div>
                  </div>
                  <a
                    href={data.plan.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sans text-[14px] font-medium text-[var(--accent-yellow-text)] hover:underline"
                  >
                    Open
                  </a>
                </div>
              </Card>
            )}
            {data.lesson_plan && (
              <Written title="Lesson plan" by={data.instructor_name ? `From ${data.instructor_name}` : null} testId="substitute-lesson-plan">
                {data.lesson_plan}
              </Written>
            )}
            {data.instructor_prep?.body && (
              <Written
                title={data.instructor_prep.title || "Prep notes"}
                by={data.instructor_name ? `From ${data.instructor_name}` : null}
                testId="substitute-instructor-prep"
              >
                {data.instructor_prep.body}
              </Written>
            )}

            {/* ---- Their notes ----------------------------------------- */}
            <SectionHeader className="mt-8">Your lesson notes</SectionHeader>
            <Card data-testid="substitute-notes">
              {editing ? (
                <>
                  <p className="font-sans text-[14px] text-[var(--text-secondary)]">
                    What you covered, how it went, and anything the instructor should pick up next time. The
                    organization reads these and decides whether families see them.
                  </p>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Title"
                    aria-label="Title"
                    maxLength={200}
                    data-testid="substitute-title"
                    className={`${inputClass} mt-4`}
                  />
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Your notes"
                    aria-label="Your notes"
                    rows={10}
                    maxLength={20000}
                    data-testid="substitute-notes-input"
                    className={`${inputClass} mt-3`}
                  />
                  {saveError && (
                    <p role="alert" className="mt-2 font-sans text-[13px] text-[var(--state-danger)]">
                      {saveError}
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Button onPress={save} disabled={saving || !notes.trim()} data-testid="substitute-save">
                      {saving ? "Saving…" : data.lesson ? "Save changes" : "Save notes"}
                    </Button>
                    {data.lesson && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(false);
                          setTitle(data.lesson.title);
                          setNotes(data.lesson.notes);
                        }}
                        className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-sans text-[16px] font-semibold text-[var(--text-primary)]">{data.lesson.title}</span>
                    <Tag accent>Saved</Tag>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-[var(--text-secondary)]" data-testid="substitute-saved-notes">
                    {data.lesson.notes}
                  </p>
                  {data.editable !== false && (
                    <div className="mt-4">
                      <Button variant="ghost" onPress={() => setEditing(true)} data-testid="substitute-edit">
                        Edit notes
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Card>
            <p className="mt-6 font-sans text-[12px] text-[var(--text-tertiary)]">
              This link is just for you
              {data.program?.ends_on ? ` and works until the program ends on ${dayLabel(data.program.ends_on, { year: true })}` : ""}.
              Please don&rsquo;t forward it.
            </p>
          </>
        )}
      </div>
    </main>
  );
}
